import React from "react";
import { Badge, Box, ButtonBase, Divider, Tooltip, Typography } from "@mui/material";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import TuneIcon from "@mui/icons-material/Tune";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import NoteAltOutlinedIcon from "@mui/icons-material/NoteAltOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import SelfImprovementIcon from "@mui/icons-material/SelfImprovement";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import { getReaderRailPositionSx } from "../../theme/readerLayout";

const RailActionButton = ({
  icon,
  label,
  tooltip,
  ariaLabel,
  onClick,
  active = false,
  badgeCount = 0,
  disabled = false,
}) => (
  <Tooltip title={tooltip || label} placement="right" arrow>
    <span>
      <ButtonBase
        onClick={onClick}
        disabled={disabled}
        aria-label={ariaLabel || tooltip || label}
        sx={{
          width: 40,
          minHeight: 44,
          py: 0.6,
          px: 0.25,
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.25,
          color: active ? "primary.main" : "text.secondary",
          bgcolor: active ? "action.selected" : "transparent",
          transition: "all 150ms ease",
          "&:hover": {
            bgcolor: "action.hover",
            color: "text.primary",
          },
          "&.Mui-disabled": {
            opacity: 0.35,
          },
        }}
      >
        <Badge
          badgeContent={badgeCount > 0 ? badgeCount : null}
          color="primary"
          sx={{
            "& .MuiBadge-badge": {
              fontSize: "0.62rem",
              height: 15,
              minWidth: 15,
              px: 0.4,
            },
          }}
        >
          {icon}
        </Badge>
        <Typography
          sx={{
            fontSize: "0.65rem",
            fontWeight: active ? 700 : 500,
            lineHeight: 1.1,
            mt: 0.25,
          }}
        >
          {label}
        </Typography>
      </ButtonBase>
    </span>
  </Tooltip>
);

const ReaderLeftRail = ({
  contentWidth = 80,
  isZenMode = false,
  edition,
  onOpenCatalog,
  onOpenSettings,
  onOpenBookmarks,
  bookmarksCount = 0,
  hasChapterBookmark = false,
  onOpenNotes,
  notesCount = 0,
  onGoHome,
  wakeLock,
  isFullscreen = false,
  isFullscreenSupported = false,
  toggleFullscreen,
  toggleZenMode,
}) => {
  const ui = edition?.ui || {};

  return (
    <Box
      component="nav"
      aria-label={ui.railCatalog || "阅读工具栏"}
      sx={getReaderRailPositionSx("left", contentWidth, { isZenMode })}
    >
      <RailActionButton
        icon={<MenuBookIcon sx={{ fontSize: 19 }} />}
        label={ui.railCatalog || "目录"}
        tooltip={`${ui.railCatalog || "目录"} (M)`}
        ariaLabel={ui.railCatalog || "目录"}
        onClick={onOpenCatalog}
      />

      <RailActionButton
        icon={<TuneIcon sx={{ fontSize: 19 }} />}
        label={ui.railSettings || "设置"}
        tooltip={`${ui.railSettings || "设置"} (S)`}
        ariaLabel={ui.railSettings || "设置"}
        onClick={(e) => onOpenSettings && onOpenSettings(e.currentTarget)}
      />

      {onOpenBookmarks && (
        <RailActionButton
          icon={
            hasChapterBookmark ? (
              <BookmarkIcon sx={{ fontSize: 19 }} />
            ) : (
              <BookmarkBorderIcon sx={{ fontSize: 19 }} />
            )
          }
          label={ui.railBookmarks || "书签"}
          tooltip={`${ui.railBookmarks || "书签"} (B)`}
          ariaLabel={ui.railBookmarks || "书签"}
          active={hasChapterBookmark}
          badgeCount={bookmarksCount}
          onClick={onOpenBookmarks}
        />
      )}

      {onOpenNotes && (
        <RailActionButton
          icon={<NoteAltOutlinedIcon sx={{ fontSize: 19 }} />}
          label={ui.railNotes || "笔记"}
          tooltip={ui.railNotes || "本章笔记"}
          ariaLabel={ui.railNotes || "笔记"}
          badgeCount={notesCount}
          onClick={onOpenNotes}
        />
      )}

      <Divider flexItem sx={{ my: 0.25 }} />

      {wakeLock?.isSupported && (
        <RailActionButton
          icon={
            wakeLock.isActive ? (
              <LightbulbIcon sx={{ fontSize: 18, color: "warning.main" }} />
            ) : (
              <LightbulbOutlinedIcon sx={{ fontSize: 18 }} />
            )
          }
          label={ui.wakeLock?.slice(0, 2) || "常亮"}
          tooltip={
            wakeLock.isEnabled
              ? ui.wakeLockActive || "屏幕常亮中 (点击关闭)"
              : ui.wakeLockInactive || "屏幕常亮已关闭 (点击开启)"
          }
          ariaLabel={
            wakeLock.isEnabled
              ? ui.wakeLockActive || "关闭屏幕常亮"
              : ui.wakeLockInactive || "开启屏幕常亮"
          }
          active={Boolean(wakeLock.isActive)}
          onClick={wakeLock.toggleEnabled}
        />
      )}

      {isFullscreenSupported && (
        <RailActionButton
          icon={
            isFullscreen ? (
              <FullscreenExitIcon sx={{ fontSize: 18 }} />
            ) : (
              <FullscreenIcon sx={{ fontSize: 18 }} />
            )
          }
          label={
            isFullscreen
              ? ui.railExitFullscreen || "退出"
              : ui.railFullscreen || "全屏"
          }
          tooltip={
            isFullscreen
              ? ui.exitFullscreen || "退出全屏 (Esc)"
              : ui.fullscreen || "全屏阅读 (F)"
          }
          ariaLabel={
            isFullscreen
              ? ui.exitFullscreen || "退出全屏"
              : ui.fullscreen || "全屏阅读"
          }
          active={isFullscreen}
          onClick={toggleFullscreen}
        />
      )}

      <RailActionButton
        icon={
          isZenMode ? (
            <CloseFullscreenIcon sx={{ fontSize: 18 }} />
          ) : (
            <SelfImprovementIcon sx={{ fontSize: 18 }} />
          )
        }
        label={
          isZenMode
            ? ui.railExitFocus || "退出"
            : ui.railFocus || "专注"
        }
        tooltip={
          isZenMode
            ? ui.exitZenMode || "退出专注 (Esc)"
            : ui.zenMode || "专注阅读 (Z)"
        }
        ariaLabel={ui.zenMode || "专注模式"}
        active={isZenMode}
        onClick={toggleZenMode}
      />

      <Divider flexItem sx={{ my: 0.25 }} />

      <RailActionButton
        icon={<HomeOutlinedIcon sx={{ fontSize: 19 }} />}
        label={ui.railHome || "首页"}
        tooltip={ui.railHome || "返回首页"}
        ariaLabel={ui.railHome || "首页"}
        onClick={onGoHome}
      />
    </Box>
  );
};

export default ReaderLeftRail;
